import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useMemo } from "react";
import { connect } from "react-redux";

const EmployeeCharts = ({ stats, currency, periods }) => {
	const employeeColumns = useMemo(() => {
		if (!stats) return [];
		const notAllowedKeys = ["weeklySalary", "weeklyHours", "annualSalary", "count", "leavers", "joiners"];
		//Flatten the array of arrays into a single array
		const flattenedColumns = stats.flatMap((entry) =>
			Object.entries(entry?.people)
				.filter(([key]) => !notAllowedKeys.includes(key))
				.map(([key, value]) => ({
					name: key
						.replace(/([A-Z])/g, " $1")
						.trim()
						.toLowerCase()
						.replace(/^\w/, (c) => c.toUpperCase()), // Format the key name
					data: Array.isArray(value) ? value : [value], // Ensure data is an array
				})),
		);

		//Group by name
		const groupedByName = flattenedColumns.reduce((acc, { name, data }) => {
			if (!acc[name]) {
				acc[name] = { name, data: [] };
			}
			acc[name].data.push(...data);
			return acc;
		}, {});

		//Convert the grouped object back into an array
		const result = Object.values(groupedByName).map(({ name, data }) => ({
			name,
			data: data.flat(), // Flatten the data arrays into a single array
		}));

		// Sort the result based on the first entry of the data array in descending order
		const sortedResult = result.sort((a, b) => {
			// Assuming the data array contains numbers. Adjust the comparison for other data types.
			const firstA = a.data[0] || 0; // Fallback to 0 if data is empty
			const firstB = b.data[0] || 0; // Fallback to 0 if data is empty
			return firstB - firstA; // For descending order
		});

		return sortedResult;
	});

	const employeeChartOptions = {
		chart: {
			type: "column",
			backgroundColor: "transparent",
		},
		credits: false,
		title: {
			text: "Total Employees",
		},
		series: employeeColumns,
		xAxis: {
			categories: periods,
		},
		yAxis: {
			min: 0,
			title: {
				text: "Employees",
			},
			stackLabels: {
				enabled: true,
			},
		},
		tooltip: {
			headerFormat: "<b>{point.x}</b><br/>",
			pointFormat: "{series.name}: {point.y}<br/>Total: {point.stackTotal}",
		},
		plotOptions: {
			column: {
				stacking: "normal",
				dataLabels: {
					enabled: true,
				},
			},
		},
	};

	const employeeWeeklySalary = useMemo(() => {
		if (!stats) return { name: "", data: [] };

		let allSalaries = [];

		stats.forEach((entry) => {
			allSalaries.push(parseFloat(entry?.people?.weeklySalary.toFixed(2)));
		});

		return { name: "Weekly Salary", data: allSalaries };
	}, [stats]);

	const employeeWeeklySalaryChartOptions = {
		chart: {
			type: "column",
			backgroundColor: "transparent",
		},
		credits: false,
		title: {
			text: "Weekly Salary",
		},
		series: [employeeWeeklySalary],
		xAxis: {
			categories: periods,
		},
		yAxis: {
			min: 0,
			title: {
				text: "Salary",
			},
			stackLabels: {
				enabled: true,
			},
		},
		tooltip: {
			headerFormat: "<b>{point.x}</b><br/>",
			pointFormat: `{series.name}: ${currency}{point.y}`,
		},
		plotOptions: {
			column: {
				pointPadding: 0.2,
				borderWidth: 0,
			},
		},
	};

	const employeeAnnualSalary = useMemo(() => {
		if (!stats) return { name: "", data: [] };

		let allSalaries = [];

		stats.forEach((entry) => {
			allSalaries.push(parseFloat(entry?.people?.annualSalary.toFixed(2)));
		});

		return { name: "Annual Salary", data: allSalaries };
	}, [stats]);

	const employeeAnnualSalaryChartOptions = {
		chart: {
			type: "column",
			backgroundColor: "transparent",
		},
		credits: false,
		title: {
			text: "Annual Salary",
		},
		series: [employeeAnnualSalary],
		xAxis: {
			categories: periods,
		},
		yAxis: {
			min: 0,
			title: {
				text: "Salary",
			},
			stackLabels: {
				enabled: true,
			},
		},
		tooltip: {
			headerFormat: "<b>{point.x}</b><br/>",
			pointFormat: `{series.name}: ${currency}{point.y}`,
		},
		plotOptions: {
			column: {
				pointPadding: 0.2,
				borderWidth: 0,
			},
		},
	};

	const employeeWeeklyHours = useMemo(() => {
		if (!stats) return { name: "", data: [] };

		let allHours = [];

		stats.forEach((entry) => {
			allHours.push(parseFloat(entry?.people?.weeklyHours.toFixed(0)));
		});

		return { name: "Weekly Hours", data: allHours };
	}, [stats]);

	const employeeWeeklyHoursChartOptions = {
		chart: {
			type: "column",
			backgroundColor: "transparent",
		},
		credits: false,
		title: {
			text: "Weekly Hours",
		},
		series: [employeeWeeklyHours],
		xAxis: {
			categories: periods,
		},
		yAxis: {
			min: 0,
			title: {
				text: "Hours",
			},
			stackLabels: {
				enabled: true,
			},
		},
		tooltip: {
			headerFormat: "<b>{point.x}</b><br/>",
			pointFormat: `{series.name}: {point.y}`,
		},
		plotOptions: {
			column: {
				pointPadding: 0.2,
				borderWidth: 0,
			},
		},
	};

	return (
		<>
			<div className="form-section col-span-3">
				<div className="form-grid col-3 col-xs-12 p-0 mt-0">
					<div className="col-span-3">
						<HighchartsReact
							highcharts={Highcharts}
							options={employeeChartOptions}
						/>
					</div>
					<div className="col-span-1">
						<HighchartsReact
							highcharts={Highcharts}
							options={employeeWeeklySalaryChartOptions}
						/>
					</div>
					<div className="col-span-1">
						<HighchartsReact
							highcharts={Highcharts}
							options={employeeAnnualSalaryChartOptions}
						/>
					</div>
					<div className="col-span-1">
						<HighchartsReact
							highcharts={Highcharts}
							options={employeeWeeklyHoursChartOptions}
						/>
					</div>
				</div>

				<div className="clearfix" />
			</div>
		</>
	);
};

const mapStateToProps = (state) => {};

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeCharts);
